import { notifyError } from '@/core/notify/notify';
import { phase1Service } from '@/core/services/phase1';
import type { JournalReversalOptions } from '@/core/models/phase1.models';
import { Button } from '@/kit/Button';
import { Icon } from '@/kit/Icon';
import { Radio, RadioGroup } from '@/kit/Radio';
import { DialogClose, DialogContent, type DialogComponentProps } from '@/kit/dialog';
import { CancelButton, useAlive, useLive, useOnInit } from './common';
import { reversalDate, reversalPeriodRange } from './reversalLinks';

export interface ReverseJournalDialogData {
  journalNo: number;
  periodId: string;
  /** The journal's own period end ('2026-03-31'): the date of a reversal in the same period, as before. */
  periodEnd: string;
}

/** What the dialog resolves with: where to post the reversal and its date. `targetPeriodId` is null for the journal's own period. */
export interface ReverseJournalChoice {
  targetPeriodId: string | null;
  reversalDate: string;
  /** True only when the journal's period is finalised and the user approved the confirmation shown for it (the API refuses without it). */
  confirmFinalisedReversal?: boolean;
}

type Where = 'same' | 'next';

interface Model {
  options: JournalReversalOptions | null;
  loading: boolean;
  where: Where;
}

const FINALISED_ADVICE =
  'This accounting period has been finalised. Remove or alter the finalisation before reversing the journal within it, or reverse it in the next accounting period.';

const NEXT_PERIOD_NOTE =
  'A reversal in the next period (for year-end adjustments) posts only into the next period’s adjusted trial balance. This period’s figures do not change.';

/**
 * APT336 (the client's answer): "allow reversing Journals in current or in next period as well, and in next period it should effect only next
 * year source TB not the previous year". Reverse asks where: this period (dated at its end, as before) or the next accounting period (dated at
 * its first day). The next period is offered only when there is one (it is never created silently) and it can take postings.
 *
 * When the journal's period is FINALISED (the client's second answer): "display a clear confirmation message asking the user to approve posting the
 * reversal on the first day of the next period. If the user does not wish to post it in the next period, advise them to remove/alter the finalisation
 * before posting the reversal within the original period." The reversal within the finalised period is not offered, the date is the first day of the
 * next period and cannot be changed, and the button says what approving it does. The API enforces the same (AP-JRN-009 / AP-JRN-010).
 */
export function ReverseJournalDialog({ data, dialogRef }: DialogComponentProps<ReverseJournalDialogData, ReverseJournalChoice>) {
  const alive = useAlive();
  const [m, set] = useLive<Model>({ options: null, loading: true, where: 'same' });

  useOnInit(() => {
    if (!data?.periodId) {
      set({ loading: false });
      return;
    }
    phase1Service
      .getReversalOptions(data.periodId)
      .then((options) => {
        if (alive.current) set({ options, loading: false });
      })
      .catch((error) => {
        // Reversing in this period still works; say that the next period could not be checked.
        if (!alive.current) return;
        set({ loading: false });
        notifyError(error);
      });
  });

  const next = m.options?.next ?? null;
  const nextAvailable = Boolean(next?.available);
  const nextReason = m.loading
    ? null
    : m.options === null
      ? 'The next accounting period could not be checked.'
      : next === null
        ? (m.options.noNextPeriodMessage ?? 'There is no next accounting period to reverse this journal into. Create the next period first.')
        : next.available
          ? null
          : (next.unavailableReason ?? 'The next accounting period cannot take postings.');

  const finalised = m.options?.finalised === true;
  const approvalNeeded = finalised && m.options?.confirmationRequired === true && nextAvailable && next !== null;
  const chosen: Where = finalised ? 'next' : m.where === 'next' && nextAvailable ? 'next' : 'same';
  const sameDate = data?.periodEnd ?? '';
  const nextDate = approvalNeeded ? (m.options?.forcedReversalDate ?? next?.startDate ?? '') : (next?.startDate ?? '');

  const onReverse = () => {
    if (approvalNeeded && next) {
      // The user has read the confirmation above and pressed the button that names the date: that is the approval the API asks for.
      dialogRef.close({ targetPeriodId: next.accountingPeriodId, reversalDate: nextDate, confirmFinalisedReversal: true });
    } else if (chosen === 'next' && next) {
      dialogRef.close({ targetPeriodId: next.accountingPeriodId, reversalDate: next.startDate });
    } else {
      dialogRef.close({ targetPeriodId: null, reversalDate: sameDate });
    }
  };

  /** A finalised period offers nothing else to press when the next period cannot take the reversal: the finalisation has to be removed or altered first. */
  const canReverse = !finalised || approvalNeeded;

  return (
    <div className="mat-dialog-wrapper new-account reverse-journal-dialog">
      <DialogContent>
        <div className="main-contain">
          <div className="sidenav-header">
            <div className="d-flex align-items-center justify-space-between">
              <h2 className="m-0">
                <span>Reverse journal #{data?.journalNo}</span>
              </h2>
              <DialogClose>
                <Icon name="close" className="close" />
              </DialogClose>
            </div>
          </div>
        </div>

        <div className="sidenav-body">
          <p className="text-gray font-size-12" role="note">
            A posted journal is never deleted. Reversing it posts a linked journal with debits and credits swapped.
          </p>

          {/* Live test of APT336: the advice was printed twice (here and under the radios) when nothing can be approved; the confirmation to approve is shown here only
              when there is something to approve, and the advice sits once under the radios. */}
          {approvalNeeded && (
            <p className="font-size-12" role="alert" data-testid="reverse-finalised-confirmation">
              <strong>{m.options?.confirmationMessage ?? FINALISED_ADVICE}</strong>
            </p>
          )}

          <RadioGroup value={chosen} onChange={(value) => set({ where: value as Where })}>
            <div className="d-flex flex-column gap-10">
              <Radio value="same" hostClassName="example-radio-button" disabled={finalised}>
                Reverse in this period <span className="text-gray">(dated {reversalDate(sameDate)})</span>
              </Radio>
              <Radio value="next" hostClassName="example-radio-button" disabled={!nextAvailable}>
                Reverse in next period
                {next && (
                  <span className="text-gray">
                    {' '}
                    ({reversalPeriodRange(next.startDate, next.endDate)}, dated {reversalDate(nextDate)})
                  </span>
                )}
              </Radio>
            </div>
          </RadioGroup>

          {finalised && (
            <p className="text-gray font-size-12" role="note" data-testid="reverse-finalised-same-period">
              {m.options?.samePeriodMessage ?? FINALISED_ADVICE}
            </p>
          )}
          {nextReason && (
            <p className="text-red font-size-12" role="note" data-testid="reverse-next-unavailable">
              {nextReason}
            </p>
          )}
          {chosen === 'next' && (
            <p className="text-gray font-size-12" role="note">
              {NEXT_PERIOD_NOTE}
            </p>
          )}
        </div>

        <div className="sidenav-footer">
          <div className="d-flex align-items-center gap-10">
            <Button className="action-button primary-button" onClick={onReverse} disabled={!canReverse}>
              {approvalNeeded ? `Approve and reverse on ${reversalDate(nextDate)}` : 'Reverse'}
            </Button>
            <CancelButton />
          </div>
        </div>
      </DialogContent>
    </div>
  );
}
